import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { Header } from 'components/common/header/Header';
import { Button } from 'components/common/button/Button';
import { MeetingRepository } from 'repository/meetingRepository';
import './MeetingCreate.css';

const STORAGE_KEY = 'meeting_create_data';

interface PlaceInfo {
    name: string;
    address: string;
    lat?: number;
    lng?: number;
}

export const TravelPlanStep = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isCreating, setIsCreating] = useState(false);

    // sessionStorage에서 저장된 데이터 로드
    const loadSavedData = () => {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        return saved ? JSON.parse(saved) : {};
    };

    const savedData = loadSavedData();

    // 여행 기간
    const [startDate, setStartDate] = useState<Date | null>(
        savedData.travelStartDate ? new Date(savedData.travelStartDate) : null
    );
    const [endDate, setEndDate] = useState<Date | null>(
        savedData.travelEndDate ? new Date(savedData.travelEndDate) : null
    );

    // 여행지 정보 (TravelDestination에서 선택된 정보)
    const destination = savedData.destination || '';
    const destinationInfo = savedData.destinationInfo || null;

    // 모임 장소 (집결지)
    const [meetingPlace, setMeetingPlace] = useState<PlaceInfo | null>(savedData.meetingPlace || null);

    // 숙박 호텔
    const [hotelPlace, setHotelPlace] = useState<PlaceInfo | null>(savedData.hotelPlace || null);

    // 숙소가 모임 장소와 동일한지
    const [isSameAsMeetingPlace, setIsSameAsMeetingPlace] = useState(savedData.isSameAsMeetingPlace || false);

    // 모임 시간
    const [meetingTime, setMeetingTime] = useState<string | null>(savedData.meetingTime || null);
    const [isCustomTime, setIsCustomTime] = useState(savedData.isCustomTime || false);

    // 달력 관련
    const today = new Date();
    const [currentYear, setCurrentYear] = useState(today.getFullYear());
    const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1);
    const [selectingDateType, setSelectingDateType] = useState<'start' | 'end' | null>(null);

    // 시간 옵션
    const timeOptions = [
        { value: '06:00', label: '오전 6시' },
        { value: '07:00', label: '오전 7시' },
        { value: '08:00', label: '오전 8시' },
        { value: '09:00', label: '오전 9시' },
        { value: '10:00', label: '오전 10시' },
        { value: '11:00', label: '오전 11시' },
        { value: '12:00', label: '낮 12시' },
        { value: '13:00', label: '오후 1시' },
        { value: '14:00', label: '오후 2시' },
        { value: '15:00', label: '오후 3시' },
        { value: '16:00', label: '오후 4시' },
        { value: '17:00', label: '오후 5시' },
        { value: '18:00', label: '오후 6시' },
    ];

    // location.state에서 장소 검색 결과 또는 시간 선택 결과 받기
    useEffect(() => {
        // 장소 검색 결과
        if (location.state?.selectedPlace && location.state?.placeInfo) {
            const placeInfo: PlaceInfo = {
                name: location.state.placeInfo.name || location.state.selectedPlace,
                address: location.state.placeInfo.address || '',
                lat: location.state.placeInfo.lat,
                lng: location.state.placeInfo.lng,
            };

            if (location.state.placeType === 'meeting') {
                setMeetingPlace(placeInfo);
                if (isSameAsMeetingPlace) {
                    setHotelPlace(placeInfo);
                }
            } else if (location.state.placeType === 'hotel') {
                setHotelPlace(placeInfo);
            }
        }

        // 시간 선택 결과 (TimePicker에서 돌아온 경우)
        if (location.state?.selectedTime) {
            setMeetingTime(location.state.selectedTime);
            setIsCustomTime(true);
        }
    }, [location.state]);

    // 숙소 동일 체크 변경 시
    useEffect(() => {
        if (isSameAsMeetingPlace && meetingPlace) {
            setHotelPlace(meetingPlace);
        }
    }, [isSameAsMeetingPlace, meetingPlace]);

    // 여행 기간 계산
    const getTravelDays = () => {
        if (!startDate || !endDate) return 0;
        const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    };

    // 데이터 변경 시 sessionStorage에 저장
    useEffect(() => {
        const dataToSave = {
            ...savedData,
            travelStartDate: startDate?.toISOString(),
            travelEndDate: endDate?.toISOString(),
            meetingPlace,
            hotelPlace,
            isSameAsMeetingPlace,
            meetingTime,
            isCustomTime,
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    }, [startDate, endDate, meetingPlace, hotelPlace, isSameAsMeetingPlace, meetingTime, isCustomTime]);

    // 날짜 선택 처리
    const handleDateSelect = (date: Date) => {
        if (selectingDateType === 'start') {
            setStartDate(date);
            if (endDate && date > endDate) {
                setEndDate(null);
            }
            setSelectingDateType('end');
        } else if (selectingDateType === 'end') {
            if (startDate && date < startDate) {
                setStartDate(date);
            } else {
                setEndDate(date);
                setSelectingDateType(null);
            }
        }
    };

    // 날짜 포맷
    const formatDate = (date: Date | null) => {
        if (!date) return '';
        return date.toLocaleDateString('ko-KR', {
            month: 'long',
            day: 'numeric',
            weekday: 'short'
        });
    };

    // 여행 기간 텍스트
    const getTravelPeriodText = () => {
        if (!startDate || !endDate) return '';
        const days = getTravelDays();
        const nights = days - 1;
        return `${nights}박 ${days}일`;
    };

    // 장소 검색 이동
    const handlePlaceSearch = (placeType: 'meeting' | 'hotel') => {
        navigate('/meeting/place-search', {
            state: {
                returnTo: '/meeting/create/travel',
                placeType,
                currentPlace: placeType === 'meeting' ? meetingPlace : hotelPlace,
            }
        });
    };

    const isNextEnabled = startDate && endDate && meetingTime && meetingPlace && (isSameAsMeetingPlace || hotelPlace);

    const handleCreate = async () => {
        if (!isNextEnabled || isCreating) return;

        setIsCreating(true);

        try {
            const allData = loadSavedData();

            // 숙소가 집결 장소와 동일한 경우 집결 장소 정보를 숙소 정보로 복사
            const actualHotelPlace = isSameAsMeetingPlace ? meetingPlace : hotelPlace;

            const createData = {
                meetingName: allData.meetingName,
                description: allData.description || '',
                category: 'TRAVEL',
                meetingType: 'TRAVEL',
                // 모임 장소 (집결지)
                placeName: meetingPlace?.name || '',
                placeAddress: meetingPlace?.address || '',
                latitude: meetingPlace?.lat || 0,
                longitude: meetingPlace?.lng || 0,
                // 모임 시간
                meetingTime: meetingTime,
                // 여행 정보
                travelDestination: destination,
                travelType: destinationInfo?.type || allData.travelType || '',  // DOMESTIC or OVERSEAS
                travelRegionId: destinationInfo?.regionId || '',  // 지역 ID (japan, jeju 등)
                travelStartDate: startDate?.toISOString(),
                travelEndDate: endDate?.toISOString(),
                // 숙박 정보 (집결 장소와 동일한 경우 집결 장소 정보 사용)
                hotelName: actualHotelPlace?.name || '',
                hotelAddress: actualHotelPlace?.address || '',
                hotelLatitude: actualHotelPlace?.lat || 0,
                hotelLongitude: actualHotelPlace?.lng || 0,
                // 기타
                maxMembers: 10,
                isPublic: true,
            };

            const meetingResponse = await MeetingRepository.onPostCreateMeeting(createData);

            const meetingId = typeof meetingResponse?.data === 'string'
                ? meetingResponse.data
                : meetingResponse?.data?.data;

            if (meetingId && typeof meetingId === 'string') {
                // sessionStorage는 TravelMeetingShare에서 정리 (데이터 표시 필요)
                const inviteCode = `MOI-${meetingId.slice(-6)}`;
                setIsCreating(false);
                // 여행 모임 전용 완료 화면으로 이동
                navigate('/meeting/travel-share', {
                    replace: true,
                    state: {
                        meeting: {
                            meetingId,
                            inviteCode
                        }
                    }
                });
            } else {
                setIsCreating(false);
                alert('여행 모임 생성에 실패했습니다.');
            }
        } catch (error) {
            console.error('여행 모임 생성 중 오류:', error);
            setIsCreating(false);
            alert('여행 모임 생성에 실패했습니다.');
        }
    };

    // 이전/다음 달
    const handlePrevMonth = () => {
        if (currentMonth === 1) {
            setCurrentMonth(12);
            setCurrentYear(currentYear - 1);
        } else {
            setCurrentMonth(currentMonth - 1);
        }
    };

    const handleNextMonth = () => {
        if (currentMonth === 12) {
            setCurrentMonth(1);
            setCurrentYear(currentYear + 1);
        } else {
            setCurrentMonth(currentMonth + 1);
        }
    };

    const renderCalendar = () => {
        const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
        const firstDayOfMonth = new Date(currentYear, currentMonth - 1, 1).getDay();

        const days = [];
        const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

        const headers = weekDays.map(day => (
            <div key={`header-${day}`} className="calendar-header-cell">{day}</div>
        ));

        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(currentYear, currentMonth - 1, day);
            const isStart = startDate && date.toDateString() === startDate.toDateString();
            const isEnd = endDate && date.toDateString() === endDate.toDateString();
            const isInRange = startDate && endDate && date > startDate && date < endDate;
            const isPast = date < new Date(today.getFullYear(), today.getMonth(), today.getDate());

            days.push(
                <button
                    key={day}
                    className={`calendar-day
                        ${isStart ? 'range-start' : ''}
                        ${isEnd ? 'range-end' : ''}
                        ${isInRange ? 'in-range' : ''}
                        ${isPast ? 'past' : ''}`}
                    onClick={() => !isPast && handleDateSelect(date)}
                    disabled={isPast}
                >
                    {day}
                </button>
            );
        }

        return (
            <div className="calendar travel-calendar">
                <div className="calendar-header">
                    <button className="calendar-nav" onClick={handlePrevMonth}>←</button>
                    <span className="calendar-month">{currentYear}년 {currentMonth}월</span>
                    <button className="calendar-nav" onClick={handleNextMonth}>→</button>
                </div>
                <div className="calendar-grid">
                    {headers}
                    {days}
                </div>
            </div>
        );
    };

    return (
        <div className="meeting-create-wrapper">
            <Header title="여행 일정" isSubPage />

            <div className="meeting-create-content">
                {/* Progress Indicator - 3 Steps */}
                <div className="progress-indicator">
                    <div className="progress-step completed">1</div>
                    <div className="progress-line active"></div>
                    <div className="progress-step completed">2</div>
                    <div className="progress-line active"></div>
                    <div className="progress-step active">3</div>
                </div>

                <div className="progress-labels three-steps">
                    <span className="progress-label completed">모임 유형</span>
                    <span className="progress-label completed">모임 정보</span>
                    <span className="progress-label active">일정 설정</span>
                </div>

                {/* 선택된 여행지 표시 */}
                {destination && (
                    <div className="selected-destination-banner">
                        <span className="destination-emoji">{destinationInfo?.emoji || '✈️'}</span>
                        <div className="destination-text">
                            <span className="destination-label">여행지</span>
                            <span className="destination-name">{destination}</span>
                        </div>
                    </div>
                )}

                {/* Title */}
                <div className="meeting-create-title-section">
                    <h1 className="meeting-create-title">기본 일정을 설정해요</h1>
                    <p className="meeting-create-subtitle">
                        여행 기간과 집결 장소, 숙소를 설정하세요.<br />
                        세부 일정은 모임 생성 후 추가할 수 있어요.
                    </p>
                </div>

                {/* 여행 기간 선택 */}
                <div className="form-section">
                    <h3 className="section-title">
                        <span className="section-icon">📅</span>
                        여행 기간
                    </h3>

                    <div className="date-range-selector">
                        <button
                            className={`date-range-button ${selectingDateType === 'start' ? 'active' : ''} ${startDate ? 'selected' : ''}`}
                            onClick={() => setSelectingDateType('start')}
                        >
                            <span className="date-label">출발</span>
                            <span className="date-value">{startDate ? formatDate(startDate) : '날짜 선택'}</span>
                        </button>
                        <div className="date-range-arrow">→</div>
                        <button
                            className={`date-range-button ${selectingDateType === 'end' ? 'active' : ''} ${endDate ? 'selected' : ''}`}
                            onClick={() => setSelectingDateType('end')}
                        >
                            <span className="date-label">도착</span>
                            <span className="date-value">{endDate ? formatDate(endDate) : '날짜 선택'}</span>
                        </button>
                    </div>

                    {startDate && endDate && (
                        <div className="travel-period-badge">
                            {getTravelPeriodText()}
                        </div>
                    )}

                    {selectingDateType && renderCalendar()}
                </div>

                {/* 모임 장소 (집결지) */}
                <div className="form-section">
                    <h3 className="section-title">
                        <span className="section-icon">📍</span>
                        모임 장소 (집결지)
                    </h3>
                    <p className="section-desc">모임원들이 처음 만나는 장소를 설정하세요.</p>

                    <div
                        className="place-select-card"
                        onClick={() => handlePlaceSearch('meeting')}
                    >
                        {meetingPlace ? (
                            <>
                                <div className="place-card-icon">📍</div>
                                <div className="place-card-content">
                                    <div className="place-card-name">{meetingPlace.name}</div>
                                    <div className="place-card-address">{meetingPlace.address}</div>
                                </div>
                                <div className="place-card-action">변경</div>
                            </>
                        ) : (
                            <>
                                <div className="place-card-icon empty">+</div>
                                <div className="place-card-content">
                                    <div className="place-card-placeholder">장소를 검색해주세요</div>
                                    <div className="place-card-hint">예: 인천공항, 서울역, 강남역</div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* 모임 시간 */}
                <div className="form-section">
                    <h3 className="section-title">
                        <span className="section-icon">⏰</span>
                        모임 시간
                    </h3>
                    <p className="section-desc">첫째 날 몇 시에 만나나요?</p>

                    <div className="time-chips">
                        {timeOptions.map((option) => (
                            <button
                                key={option.value}
                                className={`time-chip ${!isCustomTime && meetingTime === option.value ? 'selected' : ''}`}
                                onClick={() => {
                                    setIsCustomTime(false);
                                    setMeetingTime(option.value);
                                }}
                            >
                                {option.label}
                            </button>
                        ))}
                        <button
                            className="time-chip direct"
                            onClick={() => navigate('/meeting/time-picker', {
                                state: {
                                    returnTo: '/meeting/create/travel'
                                }
                            })}
                        >
                            ⏰ 직접 입력
                        </button>
                    </div>

                    {/* 직접 입력된 시간이 preset에 없으면 표시 */}
                    {isCustomTime && meetingTime && (
                        <div className="selected-custom-time">
                            <div className="custom-time-label">선택한 시간</div>
                            <div className="custom-time-value">{meetingTime}</div>
                        </div>
                    )}
                </div>

                {/* 숙박 호텔 */}
                <div className="form-section">
                    <h3 className="section-title">
                        <span className="section-icon">🏨</span>
                        숙박 장소
                    </h3>
                    <p className="section-desc">숙박할 호텔이나 숙소를 설정하세요.</p>

                    {/* 모임 장소와 동일 체크박스 */}
                    <div
                        className={`same-place-checkbox ${isSameAsMeetingPlace ? 'checked' : ''} ${!meetingPlace ? 'disabled' : ''}`}
                        onClick={() => meetingPlace && setIsSameAsMeetingPlace(!isSameAsMeetingPlace)}
                    >
                        <div className="custom-checkbox"></div>
                        <span className="checkbox-label">모임 장소와 동일</span>
                    </div>

                    {!isSameAsMeetingPlace && (
                        <div
                            className="place-select-card"
                            onClick={() => handlePlaceSearch('hotel')}
                        >
                            {hotelPlace ? (
                                <>
                                    <div className="place-card-icon">🏨</div>
                                    <div className="place-card-content">
                                        <div className="place-card-name">{hotelPlace.name}</div>
                                        <div className="place-card-address">{hotelPlace.address}</div>
                                    </div>
                                    <div className="place-card-action">변경</div>
                                </>
                            ) : (
                                <>
                                    <div className="place-card-icon empty">+</div>
                                    <div className="place-card-content">
                                        <div className="place-card-placeholder">숙소를 검색해주세요</div>
                                        <div className="place-card-hint">호텔, 펜션, 에어비앤비 등</div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {isSameAsMeetingPlace && meetingPlace && (
                        <div className="same-place-info">
                            <div className="place-card-icon">🏨</div>
                            <div className="place-card-content">
                                <div className="place-card-name">{meetingPlace.name}</div>
                                <div className="place-card-address">{meetingPlace.address}</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* 안내 */}
                <div className="travel-tips">
                    <div className="tip-header">
                        <span className="tip-icon">💡</span>
                        <span className="tip-title">안내</span>
                    </div>
                    <ul className="tip-list">
                        <li>세부 일정(관광지, 맛집 등)은 모임 생성 후 상세 화면에서 추가할 수 있어요.</li>
                        <li>모임원들과 함께 일정을 편집하고 의견을 나눌 수 있어요.</li>
                        <li>AI 추천 기능으로 인기 장소를 추천받을 수 있어요.</li>
                    </ul>
                </div>
            </div>

            {/* Create Button */}
            <div className="meeting-create-footer">
                <Button
                    text={isCreating ? "생성 중..." : "여행 모임 만들기"}
                    disabled={!isNextEnabled || isCreating}
                    onPress={handleCreate}
                />
            </div>
        </div>
    );
};
